import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, ShieldCheck, Upload, RefreshCw, Stethoscope } from 'lucide-react';
import { Button } from '@components/Button';
import { Input, Select } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { checkingAccountService } from '@services/checkingAccountService';
import bankIntegrationService from '@services/bankIntegrationService';
import companyService from '@services/companyService';
import { CheckingAccountFormData, Bank } from '@/types/checkingAccount';
import { BankIntegration, BankIntegrationFormData } from '@/types/bankIntegration';
import { Company, formatCnpj } from '@/types/company';
import { formatDateBR } from '@pages/financial/financialUtils';
import { certificateBadgeClass, certificateStatus, daysUntilISO } from '@/utils/certificateStatus';
import { CertificateLog } from '@components/certificates/CertificateLog';
import { CertificateResultCard } from '@components/certificates/CertificateResultCard';
import { SecretField } from '@components/certificates/SecretField';
import { CertificateResult } from '@/types/certificate';

const emptyForm: CheckingAccountFormData = {
  companyId: 0,
  accountsDescription: '',
  bankNumber: '',
  branchNumber: null,
  accountNumber: null,
  accountNumberDigit: null,
  viewIndex: null,
};

/** Converte um campo numérico opcional ('' → null). */
const toOptionalNumber = (value: string): number | null =>
  value.trim() === '' ? null : Number(value);

// URLs de produção do Itaú — pré-preenchidas porque hoje só há um provider;
// ficam editáveis para homologação ou mudança de endpoint sem deploy.
const ITAU_TOKEN_URL = 'https://sts.itau.com.br/api/oauth/token';
const ITAU_API_BASE_URL = 'https://account-statement.api.itau.com/account-statement/v1';

const emptyIntegrationForm: BankIntegrationFormData = {
  provider: 'itau',
  clientId: '',
  clientSecret: null,
  tokenUrl: ITAU_TOKEN_URL,
  apiBaseUrl: ITAU_API_BASE_URL,
  isActive: false,
};

/**
 * Integração bancária (API de Extrato do Itaú) da conta.
 *
 * Vive no cadastro da conta porque credencial e certificado são atributos DELA.
 * O .pfx vai para o Storage via API, e três ações operam sobre ele:
 *
 * - Substituir: sobe um .pfx que você já tem.
 * - Renovar: o ERP fala com o STS do Itaú, gera o certificado novo e o coloca
 *   em uso — o mesmo processo do certificado do PIX. Só é aceito nos últimos
 *   30 dias antes do vencimento; fora disso o banco devolve C700 e nada muda.
 * - Testar: confere se o certificado gravado ainda autentica no Itaú.
 *
 * O Client Secret e a senha do certificado ficam visíveis (atrás de um clique):
 * são necessários para diagnosticar com curl fora do sistema, e esta tela já é
 * restrita a quem opera a integração.
 */
const BankIntegrationSection: React.FC<{ accountId: number }> = ({ accountId }) => {
  const [integration, setIntegration] = useState<BankIntegration | null>(null);
  const [form, setForm] = useState<BankIntegrationFormData>(emptyIntegrationForm);
  const [secretInput, setSecretInput] = useState('');
  const [certPassword, setCertPassword] = useState('');
  const [certFile, setCertFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<'upload' | 'test' | 'renew' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [result, setResult] = useState<CertificateResult | null>(null);
  const [confirmRenew, setConfirmRenew] = useState(false);
  // Incrementado a cada operação concluída, para o histórico recarregar.
  const [logVersion, setLogVersion] = useState(0);

  const load = async () => {
    const data = await bankIntegrationService.getIntegration(accountId);
    setIntegration(data);
    if (data) {
      setForm({
        provider: data.provider,
        clientId: data.clientId,
        clientSecret: null,
        tokenUrl: data.tokenUrl,
        apiBaseUrl: data.apiBaseUrl,
        isActive: data.isActive,
      });
    }
  };

  useEffect(() => {
    load().catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar a integração'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const handleSave = async () => {
    if (!form.clientId.trim()) {
      setError('Informe o Client ID');
      return;
    }
    if (!integration?.hasClientSecret && !secretInput.trim()) {
      setError('Informe o Client Secret para criar a integração');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setNotice(null);
      const saved = await bankIntegrationService.saveIntegration(accountId, {
        ...form,
        // Campo vazio no update = mantém o segredo atual.
        clientSecret: secretInput.trim() === '' ? null : secretInput.trim(),
      });
      setIntegration(saved);
      setSecretInput('');
      setNotice('Integração salva.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar a integração');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Roda uma operação de certificado e recarrega a integração depois.
   *
   * O teste não altera nada, então não recarrega — mas conta para o histórico,
   * porque cada teste também deixa rastro na trilha.
   */
  const run = async (kind: 'upload' | 'test' | 'renew', fn: () => Promise<CertificateResult>) => {
    try {
      setBusy(kind);
      setError(null);
      setNotice(null);
      setResult(null);
      const res = await fn();
      setResult(res);
      setLogVersion((v) => v + 1);
      if (kind !== 'test') await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao executar a operação');
    } finally {
      setBusy(null);
    }
  };

  const handleUpload = () => {
    if (!certFile) {
      setError('Escolha o arquivo .pfx do certificado');
      return;
    }
    if (!certPassword) {
      setError('Informe a senha do certificado');
      return;
    }
    const file = certFile;
    const pass = certPassword;
    run('upload', async () => {
      const res = await bankIntegrationService.uploadCertificate(accountId, file, pass);
      setCertFile(null);
      setCertPassword('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return res;
    });
  };

  const handleTest = () => run('test', () => bankIntegrationService.testCertificate(accountId));

  const handleRenew = () => {
    setConfirmRenew(false);
    run('renew', () => bankIntegrationService.renewCertificate(accountId));
  };

  const status = certificateStatus(integration?.certificateExpiresAt);
  const days = integration?.certificateExpiresAt ? daysUntilISO(integration.certificateExpiresAt) : undefined;

  const expiryBadge = () => {
    if (!integration) return null;
    // O terceiro estado importa: uma integração PODE ter certificado sem que o
    // ERP saiba a validade (linha migrada antes de o upload passar a gravá-la).
    // O badge antigo devolvia null nesse caso e a tela ficava muda.
    const text =
      status === 'expired'
        ? `Vencido em ${formatDateBR(integration.certificateExpiresAt)}`
        : status === 'ok' || status === 'expiring'
          ? `Válido até ${formatDateBR(integration.certificateExpiresAt)}`
          : integration.hasCertificate
            ? 'Validade não registrada'
            : 'Sem certificado';
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${certificateBadgeClass(status)}`}
      >
        <ShieldCheck size={13} /> {text}
      </span>
    );
  };

  return (
    <Card>
      <CardHeader title="Integração bancária (Itaú)" />
      <CardContent>
        <p className="text-sm text-slate-600 mb-4">
          Habilita a consulta do extrato direto na API do banco e a aba de conciliação na tela de
          Conta Corrente. O certificado mTLS (.pfx) é validado e guardado no Storage — renovar é um
          clique aqui, sem secret de k8s e sem redeploy.
        </p>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {notice && <p className="text-sm text-emerald-700 mb-3">{notice}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Input
              label="Client ID"
              value={form.clientId}
              onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value }))}
              placeholder="ce24c76c-…"
              maxLength={100}
            />
            <p className="text-xs text-slate-500 mt-1">
              É também o CN do certificado — o Itaú emite os dois com o mesmo valor.
            </p>
          </div>
          <div>
            <Input
              label={integration?.hasClientSecret ? 'Trocar o Client Secret' : 'Client Secret'}
              type="password"
              value={secretInput}
              onChange={(e) => setSecretInput(e.target.value)}
              placeholder={integration?.hasClientSecret ? 'deixe vazio para manter o atual' : ''}
              maxLength={500}
            />
          </div>
          <div>
            <Input
              label="URL do token (STS)"
              value={form.tokenUrl}
              onChange={(e) => setForm((p) => ({ ...p, tokenUrl: e.target.value }))}
              maxLength={300}
            />
          </div>
          <div>
            <Input
              label="URL da API de extrato"
              value={form.apiBaseUrl}
              onChange={(e) => setForm((p) => ({ ...p, apiBaseUrl: e.target.value }))}
              maxLength={300}
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input
              id="bank-integration-active"
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              checked={form.isActive}
              onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
            />
            <label htmlFor="bank-integration-active" className="text-sm text-slate-700">
              Integração ativa (a conciliação e o job diário só rodam com ela ligada)
            </label>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button size="sm" onClick={handleSave} loading={saving} icon={<Save size={16} />}>
            Salvar integração
          </Button>
        </div>

        {/* Certificado: só faz sentido depois da integração existir. */}
        {integration && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <p className="text-sm font-medium text-slate-800">Certificado mTLS (.pfx)</p>
              {expiryBadge()}
            </div>
            {integration.hasCertificate && integration.certificateUploadedAt && (
              <p className="text-xs text-slate-500 mb-3">
                Último envio em {formatDateBR(integration.certificateUploadedAt)}
                {integration.certificateUploadedBy ? ` por ${integration.certificateUploadedBy}` : ''}.
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <SecretField
                label="Client Secret"
                value={integration.clientSecret}
                emptyLabel="Sem segredo gravado"
                hint="Não muda na renovação — é o mesmo desde a emissão da credencial."
              />
              <SecretField
                label="Senha do certificado gravado"
                value={integration.certificatePassword}
                emptyLabel="Sem senha gravada"
                hint="Abre o .pfx que está no Storage. Serve para conferir com openssl ou curl."
              />
            </div>

            {result && <CertificateResultCard result={result} />}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Arquivo .pfx</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pfx,.p12"
                  onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-emerald-700 hover:file:bg-emerald-100"
                />
              </div>
              <div>
                <Input
                  label="Senha do arquivo enviado"
                  type="password"
                  value={certPassword}
                  onChange={(e) => setCertPassword(e.target.value)}
                />
              </div>
              <div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUpload}
                  loading={busy === 'upload'}
                  disabled={busy !== null}
                  icon={<Upload size={16} />}
                >
                  {integration.hasCertificate ? 'Substituir certificado' : 'Enviar certificado'}
                </Button>
              </div>
            </div>

            {integration.hasCertificate && (
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleTest}
                  loading={busy === 'test'}
                  disabled={busy !== null}
                  icon={<Stethoscope size={16} />}
                >
                  Testar certificado
                </Button>

                {confirmRenew ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-slate-700">
                      Renovar agora no Itaú e passar a usar o certificado novo?
                    </span>
                    <Button size="sm" onClick={handleRenew} loading={busy === 'renew'} disabled={busy !== null}>
                      Confirmar
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setConfirmRenew(false)}
                      disabled={busy !== null}
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setConfirmRenew(true)}
                    disabled={busy !== null}
                    icon={<RefreshCw size={16} />}
                  >
                    Renovar certificado
                  </Button>
                )}
              </div>
            )}

            <p className="text-xs text-slate-500 mt-3">
              O arquivo é validado com a senha antes de ser aceito e precisa pertencer ao Client ID
              desta conta. A renovação só é aceita pelo Itaú nos últimos 30 dias antes do vencimento
              {days !== undefined ? ` (faltam ${days} dia(s))` : ''}; antes disso o banco responde
              que o certificado atual continua válido.
            </p>

            <CertificateLog
              fetchLogs={() => bankIntegrationService.getCertificateLogs(accountId)}
              refreshKey={logVersion}
              emptyLabel="Nenhuma operação registrada ainda para esta conta."
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const CheckingAccountFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [companies, setCompanies] = useState<Company[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [formData, setFormData] = useState<CheckingAccountFormData>(emptyForm);

  useEffect(() => {
    Promise.all([companyService.getCompanies(), checkingAccountService.listBanks()])
      .then(([cs, bs]) => {
        setCompanies(cs);
        setBanks(bs);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar dados de apoio'));
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      loadData(parseInt(id));
    }
  }, [id, isEditing]);

  const loadData = async (itemId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await checkingAccountService.getById(itemId);
      setFormData({
        companyId: data.companyId,
        accountsDescription: data.accountsDescription,
        bankNumber: data.bankNumber ?? '',
        branchNumber: data.branchNumber,
        accountNumber: data.accountNumber,
        accountNumberDigit: data.accountNumberDigit,
        viewIndex: data.viewIndex,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const updateField = <K extends keyof CheckingAccountFormData>(field: K, value: CheckingAccountFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyId) newErrors.companyId = 'Empresa é obrigatória';
    if (!formData.accountsDescription.trim()) {
      newErrors.accountsDescription = 'Descrição é obrigatória';
    } else if (formData.accountsDescription.length > 100) {
      newErrors.accountsDescription = 'Descrição deve ter no máximo 100 caracteres';
    }
    if (!formData.bankNumber.trim()) newErrors.bankNumber = 'Banco é obrigatório';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      if (isEditing && id) {
        await checkingAccountService.update(parseInt(id), formData);
      } else {
        await checkingAccountService.create(formData);
      }

      navigate('/settings/financial/checking-accounts');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/settings/financial/checking-accounts')}>
            <ArrowLeft size={20} />
          </Button>
          <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/settings/financial/checking-accounts')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">{isEditing ? 'Editar Conta Corrente' : 'Nova Conta Corrente'}</h1>
          <p className="text-slate-600">
            {isEditing ? 'Altere os dados da conta corrente' : 'Preencha os dados da nova conta corrente'}
          </p>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <Card>
        <CardHeader title="Dados da Conta" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Empresa */}
            <div className="md:col-span-2">
              <Select
                label="Empresa"
                required
                value={formData.companyId || ''}
                error={errors.companyId}
                disabled={isEditing}
                onChange={(e) => updateField('companyId', Number(e.target.value))}
              >
                <option value="">Selecione a empresa</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {formatCnpj(c.document)} — {c.name}
                  </option>
                ))}
              </Select>
              {isEditing && (
                <p className="text-xs text-slate-500 mt-1">A empresa não pode ser alterada após a criação.</p>
              )}
            </div>

            {/* Descrição */}
            <div className="md:col-span-2">
              <Input
                label="Descrição"
                required
                value={formData.accountsDescription}
                error={errors.accountsDescription}
                onChange={(e) => updateField('accountsDescription', e.target.value)}
                placeholder="Ex: Itaú Conta Corrente"
                maxLength={100}
              />
            </div>

            {/* Banco */}
            <div>
              <Select
                label="Banco"
                required
                value={formData.bankNumber}
                error={errors.bankNumber}
                onChange={(e) => updateField('bankNumber', e.target.value)}
              >
                <option value="">Selecione o banco</option>
                {banks.map((b) => (
                  <option key={b.bankNumber} value={b.bankNumber}>
                    {b.bankNumber} — {b.bankName ?? b.bankDescription ?? ''}
                  </option>
                ))}
              </Select>
            </div>

            {/* Agência */}
            <div>
              <Input
                label="Agência"
                type="number"
                value={formData.branchNumber ?? ''}
                onChange={(e) => updateField('branchNumber', toOptionalNumber(e.target.value))}
                placeholder="Ex: 1234"
              />
            </div>

            {/* Número da conta */}
            <div>
              <Input
                label="Número da conta"
                value={formData.accountNumber ?? ''}
                onChange={(e) => updateField('accountNumber', e.target.value.trim() === '' ? null : e.target.value)}
                placeholder="Ex: 56789"
                maxLength={15}
              />
            </div>

            {/* Dígito */}
            <div>
              <Input
                label="Dígito"
                type="number"
                value={formData.accountNumberDigit ?? ''}
                onChange={(e) => updateField('accountNumberDigit', toOptionalNumber(e.target.value))}
                placeholder="Ex: 0"
              />
            </div>

            {/* Ordem de exibição */}
            <div>
              <Input
                label="Ordem de exibição"
                type="number"
                value={formData.viewIndex ?? ''}
                onChange={(e) => updateField('viewIndex', toOptionalNumber(e.target.value))}
                helperText="Ordena a conta nas listagens e seletores."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/settings/financial/checking-accounts')}>
          Cancelar
        </Button>
        <Button onClick={handleSave} loading={saving} icon={<Save size={18} />}>
          {isEditing ? 'Salvar Alterações' : 'Criar Conta'}
        </Button>
      </div>

      {/* Integração bancária: só em edição — precisa do AccountId, e cadastrar
          credencial antes de a conta existir não faz sentido. Salvamento é
          independente do CRUD acima (endpoints próprios). */}
      {isEditing && id && <BankIntegrationSection accountId={parseInt(id)} />}
    </div>
  );
};
